import * as React from 'react';
import { X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '@/lib/utils';
import { DialogOverlay } from '@/components/ui/dialog';

/**
 * Side sheet — a detail panel that slides in from the right at `sm+` and is a
 * bottom sheet on mobile (same as <DialogContent>). Built on the Dialog
 * primitive, so focus trapping, Escape and scroll lock come for free.
 *
 * Use it when a row's detail is too wide to live inside the row: an expanded
 * `<tr>` is as wide as the table, so on a wide table its actions end up
 * off-screen behind a horizontal scroll.
 */
const Sheet = DialogPrimitive.Root;
const SheetTitle = DialogPrimitive.Title;
const SheetDescription = DialogPrimitive.Description;

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: bottom sheet, like DialogContent.
        'fixed bottom-0 left-0 right-0 z-50 flex max-h-[92vh] flex-col rounded-t-2xl border bg-popover shadow-overlay outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
        'duration-200 motion-reduce:!duration-0',
        // sm+: full-height panel on the right.
        'sm:bottom-0 sm:left-auto sm:top-0 sm:max-h-none sm:w-full sm:max-w-3xl sm:rounded-none sm:border-y-0 sm:border-r-0',
        'sm:data-[state=open]:slide-in-from-right sm:data-[state=closed]:slide-out-to-right',
        'sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-2"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = 'SheetContent';

export { Sheet, SheetContent, SheetTitle, SheetDescription };
